import {
  IStateMetaData,
  IMachineContext,
  TMachineActions,
  TInputTypes,
  TMachineStates,
  IYardageSelectionEvent,
  TRefEventStates,
  TMachineEvents,
  TRefEventsActions,
  TPenaltyDownResult,
} from '@/types';
import type { AnyEventObject, BaseActionObject, StateNodeConfig } from 'xstate';
import { MAIN_MACHINE_ID, SELECT_DOWN_INPUTS, TEventTypes, YARDAGE_SELECTOR_BUTTONS } from '@/constants';
import { TFieldGoalEvents, TFieldGoalStates } from '@/types/workflow/fieldGoalMachine';
import { HANDLE_YARDAGE_TOGGLES_META, handleFumbleResult, handleYardageToggle } from '../utils';
import { FIELD_GOAL_REF_WF } from './refEventFlows/fieldGoal';
import { onFGConfirmedSelected, onFGResultSelected, onFGSelected } from './nextActions/fieldGoal';

export const FG_MACHINE: StateNodeConfig<IMachineContext, any, AnyEventObject, BaseActionObject> = {
  initial: TFieldGoalStates.START_FIELD_GOAL,
  states: {
    [TFieldGoalStates.START_FIELD_GOAL]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Field Goal',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onFGSelected,
        onBack({ send }) {
          send(TFieldGoalEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TFieldGoalEvents.BACK]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_FG]: {
          target: TFieldGoalStates.FIELD_GOAL_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_FAKE]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_FUMBLE]: {
          target: TFieldGoalStates.FIELD_GOAL_FUMBLE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_REF]: {
          target: TFieldGoalStates.FIELD_GOAL_REF_EVT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.PENALTY_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}.${TFieldGoalStates.FIELD_GOAL_REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TFieldGoalStates.FIELD_GOAL_RESULT]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Field Goal Result',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onFGResultSelected,
        onBack({ send }) {
          send(TFieldGoalEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TFieldGoalEvents.BACK]: {
          target: TFieldGoalStates.START_FIELD_GOAL,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_FUMBLE]: {
          target: TFieldGoalStates.FIELD_GOAL_FUMBLE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_REF]: {
          target: TFieldGoalStates.FIELD_GOAL_REF_EVT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_FG]: {
          target: TFieldGoalStates.FIELD_GOAL_CONFIRM,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.ON_RETURN]: {
          target: TFieldGoalStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.PENALTY_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}.${TFieldGoalStates.FIELD_GOAL_REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TFieldGoalStates.FIELD_GOAL_FUMBLE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Fumble Result',
          showLogo: true,
          showBackBtn: true,
        },
        onNext({ send, generalState, playFlowState, payload, context }) {
          playFlowState.pushEvent(
            {
              eventTypeId: TEventTypes.POTENTIAL_TOV,
              value: true,
            },
            generalState.currentGame.gameId
          );

          handleFumbleResult(
            TFieldGoalEvents.ON_FUMBLE_RECOVERY,
            context.inputForNextComponent.backTo,
            TFieldGoalEvents.BACK_TO_FUMBLE_RECOVERY,
            payload,
            send,
            playFlowState,
            generalState,
            context
          );
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TFieldGoalEvents.ON_FUMBLE_RECOVERY]: {
          target: TFieldGoalStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.TO_RESULT]: {
          target: TFieldGoalStates.FIELD_GOAL_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.TO_START]: {
          target: TFieldGoalStates.START_FIELD_GOAL,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TFieldGoalStates.SELECT_YARDAGE]: {
      meta: {
        component: TInputTypes.YARDAGE_SELECTOR,
        componentConfiguration: {
          title: 'Field Goal Returned',
          showLogo: true,
          showBackBtn: true,
        },
        onNext(inp) {
          const { payload } = inp;
          const { yardLine, side } = payload as IYardageSelectionEvent;

          handleYardageToggle(inp, TFieldGoalEvents.BACK, {
            side,
            yardLine,
            currToggle: 0,
          });
        },
        onBack({ send, context }) {
          if (context.currentPlay.penalty) {
            send(TRefEventsActions.BACK_TO_PENALTY);
          } else {
            send(TFieldGoalEvents.BACK, {});
          }
        },
      } as IStateMetaData,
      on: {
        [TFieldGoalEvents.BACK]: {
          target: TFieldGoalStates.FIELD_GOAL_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}.${TFieldGoalStates.FIELD_GOAL_REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TFieldGoalStates.FIELD_GOAL_CONFIRM,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          target: TFieldGoalStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TFieldGoalStates.HANDLE_YARDAGE_TOGGLES]: {
      meta: HANDLE_YARDAGE_TOGGLES_META,
      on: {
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}.${TFieldGoalStates.SELECT_YARDAGE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.UNSET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.POST_PENALTY_YARDAGE]: {
          target: TFieldGoalStates.SELECT_YARDAGE,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.BACK_TO_SAME]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TFieldGoalStates.FIELD_GOAL_CONFIRM,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TFieldGoalStates.FIELD_GOAL_CONFIRM]: {
      meta: {
        component: TInputTypes.CONFIRMATION_SELECTOR,
        componentConfiguration: {
          title: 'Field Goal',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onFGConfirmedSelected,
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: TFieldGoalStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_HANDLE_YARDAGE]: {
          target: TFieldGoalStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}.${TFieldGoalStates.FIELD_GOAL_REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.TO_RESULT]: {
          target: TFieldGoalStates.FIELD_GOAL_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.ON_REF_EVENT]: {
          target: TFieldGoalStates.FIELD_GOAL_REF_EVT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.CONFIRM_MISS]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
        [TFieldGoalEvents.CONFIRM_MAKE]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
        [TFieldGoalEvents.FG_MAKE_OVERTIME]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
        [TFieldGoalEvents.CONFIRM_TD]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
      },
    },

    [TFieldGoalStates.FIELD_GOAL_REF_EVT]: FIELD_GOAL_REF_WF,

    [TFieldGoalStates.SELECT_POSSESSION]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Select Possession',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: ({ context, generalState, payload, playFlowState, send }) => {
          const ctxUpdate: Partial<IMachineContext> = {
            currentPlay: {
              ...context.currentPlay,
              resultType: context.currentPlay?.resultType || [],
              options: {
                ...context.currentPlay.options,
              },
            },
          };

          ctxUpdate.currentPlay.options.isPenaltyDuringReturn = true;

          // if possession is to the kicking team, go to replay/first down screen
          if (payload.value === playFlowState.activeTeam) {
            ctxUpdate.currentPlay.options.isPossessionRetained = true;

            return send(TFieldGoalEvents.POSSESSION_SELECTED, {
              ctxUpdate,
              nextStateInput: {
                ...SELECT_DOWN_INPUTS,
                backTo: TFieldGoalEvents.BACK_TO_SELECT_POSSESSION,
                title: 'Select Down',
              },
            });
          }

          return send(TFieldGoalEvents.TO_YARDAGE, {
            ctxUpdate,
            nextStateInput: {
              buttons: YARDAGE_SELECTOR_BUTTONS(),
              sliderLabels: {
                left: generalState.teams[playFlowState.teams.left].abbreviation,
                right: generalState.teams[playFlowState.teams.right].abbreviation,
              },
              backTo: TRefEventsActions.BACK_TO_PENALTY,
              title: 'Post-Penalty Yardline',
            },
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}.${TFieldGoalStates.FIELD_GOAL_REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.POSSESSION_SELECTED]: {
          target: TFieldGoalStates.SELECT_DOWN_POST_PENALTY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.TO_YARDAGE]: {
          target: TFieldGoalStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TFieldGoalStates.SELECT_DOWN_POST_PENALTY]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Select Down',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: ({ context, generalState, payload, playFlowState, send }) => {
          const ctxUpdate: Partial<IMachineContext> = {
            currentPlay: {
              ...context.currentPlay,
              resultType: context.currentPlay?.resultType || [],
              options: {
                ...context.currentPlay.options,
              },
            },
          };

          if (payload.value === TPenaltyDownResult.REPLAY_DOWN) {
            ctxUpdate.currentPlay.options.isReplayDown = true;
            ctxUpdate.currentPlay.isFirstDown = false;
          } else if (payload.value === TPenaltyDownResult.FIRST_DOWN) {
            ctxUpdate.currentPlay.options.isReplayDown = false;
            ctxUpdate.currentPlay.isFirstDown = true;
          }

          return send(TFieldGoalEvents.TO_YARDAGE, {
            ctxUpdate,
            nextStateInput: {
              buttons: YARDAGE_SELECTOR_BUTTONS(),
              sliderLabels: {
                left: generalState.teams[playFlowState.teams.left].abbreviation,
                right: generalState.teams[playFlowState.teams.right].abbreviation,
              },
              backTo: TRefEventsActions.BACK_TO_PENALTY,
              title: 'Post-Penalty Yardline',
            },
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TFieldGoalEvents.BACK_TO_SELECT_POSSESSION]: {
          target: TFieldGoalStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TFieldGoalEvents.TO_YARDAGE]: {
          target: TFieldGoalStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
  },
};
